import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'

import {
  InteractivePlanType,
  LotFilterInterface,
  ProjectIdentity,
  RootStateTypeExtra,
} from '@store/types'

import Container from '@components/container/v2'
import DataHandler from '@components/data-handler'
import { Status } from '@components/data-handler/data-handler'
import useGetFilterData from '@components/filter/use-get-filter-data'
import FILTER_INITIAL_STATE from '@components/filter/v2/filterStateHouseAndLand'
import Filter from '@components/filter/v2/house-and-land-filter'
import ImageHandler from '@components/image-handler'
import { CanvasInteractive } from '@components/showcase-canvas'
import { CanvasRefInterface } from '@components/showcase-canvas/canvas-interactive'
import { MarkerType } from '@components/showcase-canvas/constants'
import { Polygon } from '@components/showcase-canvas/types'
import ZoneDropdown from '@components/zone-dropdown/v2'

import {
  MatrixDataCollectionInterface,
  PrecinctListItemInterface,
  StageCollectionInterface,
  selectMatrixDataFromResult,
  useGetMatrixDataByPrecinctQuery,
} from '@api/houseAndLand'
import {
  MapContent,
  MappingBlockCollection,
  selectFromResult as selectFromResultInteractive,
  useGetInteractivePlanQuery,
} from '@api/interactive-plan'

import styleUtil from '@utilities/style-util'

import FirebaseControlQuery from '@firebaseUtil/firebase-control-query'
import { SessionMap } from '@firebaseUtil/types'

import { ArrowSvg, FilterSvg } from '@svg/react'

import filterPackageSummary from '@adUtilities/filter-package-summary'

import PrecinctSkeleton from './precinct-skeleton'

export interface PrecinctProps {
  session: SessionMap | undefined
  projectIdentity: ProjectIdentity
  precinctList: PrecinctListItemInterface[]
}

const findFilterStages = (
  matrixData: MatrixDataCollectionInterface,
  lotFilter: LotFilterInterface,
  activePrecinct: string,
  availableStatusLabel: string
): StageCollectionInterface => {
  const activeMatrixData = matrixData[activePrecinct]
  if (!activeMatrixData || Object.keys(activeMatrixData)?.length === 0) {
    return {}
  }
  if (!lotFilter?.apply) {
    return activeMatrixData.stages
  }
  const filteredMatrixData = filterPackageSummary(
    activeMatrixData,
    {
      ...lotFilter,
      storey: lotFilter.storey.map((item) => Number(item)),
    },
    availableStatusLabel
  )
  if (!filteredMatrixData) {
    return {}
  }
  return filteredMatrixData.stages || {}
}

const ARROW_TYPE = 'arrow'
const ARROW_SMALL_TYPE = 'arrow-small'

function Precinct({ session, projectIdentity, precinctList }: PrecinctProps) {
  const themeData = styleUtil()

  const firebaseControlQuery = FirebaseControlQuery({
    ...projectIdentity,
    sessionKey: projectIdentity.sessionId,
  })

  const [storeActivePrecinct, setActivePrecinct] = useState<string>('')
  const { statusLabels } = projectIdentity

  const [hasSessionInitialized, setHasSessionInitialized] = useState(false)

  const matrixQueryResult = useGetMatrixDataByPrecinctQuery(
    {
      projectName: projectIdentity.projectName,
      precinctIdOrLabel: storeActivePrecinct,
    },
    {
      selectFromResult: selectMatrixDataFromResult,
      skip: !storeActivePrecinct,
    }
  )

  const lotFilter = useMemo(
    () => session?.houseAndLand?.lotFilter || FILTER_INITIAL_STATE,
    [session?.houseAndLand?.lotFilter]
  )

  const levelMarkerColour = projectIdentity.markerColourSettings
  const matrixData = {
    [storeActivePrecinct]: matrixQueryResult.matrixData,
  }

  const filterStages = findFilterStages(
    matrixData,
    lotFilter,
    storeActivePrecinct,
    statusLabels.available
  )

  const canvasContainerRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<CanvasRefInterface>()
  const [isFilterOpen, toggleFilter] = useState(false)
  const [rendering, setRendering] = useState(false)
  const [renderCanvas, setRenderCanvas] = useState(false)
  const [isCanvasImageLoaded, setImageLoaded] = useState(false)

  const prevActivePrecinctRef = useRef(storeActivePrecinct)
  const [hasLoaded, setHasLoaded] = useState(false)
  const [filterActivePrecinct, setFilterActivePrecinct] = useState('')

  useGetFilterData({
    projectName: projectIdentity.projectId,
    precinctId: filterActivePrecinct,
  })

  const interactiveQueryResult = useGetInteractivePlanQuery(
    {
      projectName: projectIdentity.projectName,
      type: InteractivePlanType.Precinct,
      slug: storeActivePrecinct,
    },
    {
      selectFromResult: selectFromResultInteractive,
    }
  )

  const interactiveAreaViewQueryResult = useGetInteractivePlanQuery(
    {
      projectName: projectIdentity.projectName,
      type: InteractivePlanType.AreaView,
    },
    {
      selectFromResult: selectFromResultInteractive,
    }
  )

  const storePrecincts = useMemo<MappingBlockCollection>(() => {
    const { maps } = interactiveQueryResult
    if (maps.precinct && Object.keys(maps.precinct)?.length > 0) {
      return maps.precinct
    }
    return {}
  }, [interactiveQueryResult])

  const getAvailableLotCount = (poly: Polygon) => {
    const splicedGroupId = poly.groupId.split('-')
    const foundStage = filterStages[splicedGroupId[1] || poly.groupId]
    if (foundStage) {
      return foundStage.lots?.filter(
        (res) => res.status === statusLabels?.available
      ).length
    }
    return 0
  }

  const getStageStatus = useCallback(
    (poly: Polygon) => {
      const splicedGroupId = poly.groupId.split('-')

      if (!lotFilter?.apply) {
        return false
      }
      const foundStage = filterStages[splicedGroupId[1] || poly.groupId]

      if (!foundStage) {
        return false
      }

      return foundStage.lots?.length > 0
    },
    [lotFilter, filterStages]
  )

  const getStageLotCount = useCallback(
    (poly: Polygon) => {
      const splicedGroupId = poly.groupId.split('-')
      const foundStage = filterStages[splicedGroupId[1] || poly.groupId]

      if (!foundStage) return 0

      return foundStage.lots?.length
    },
    [lotFilter, filterStages]
  )

  const findRelevantStage = useCallback(
    (stageName: string) =>
      matrixData?.[storeActivePrecinct]?.stages?.[stageName]?.lots,
    [matrixData, storeActivePrecinct]
  )

  const checkGroupId = useCallback((groupId: string) => {
    const splitGroupString = groupId.split('-')
    if (splitGroupString.length > 1) {
      const [activePrecinct, activeLevel] = splitGroupString
      return {
        activePrecinct,
        activeLevel,
      }
    }
    return {
      activeLevel: groupId,
      activePrecinct: '',
    }
  }, [])

  const interactiveClick = useCallback(
    async (poly: Polygon) => {
      await firebaseControlQuery.update({
        [`houseAndLand.activeStageId`]: poly.groupId,
      })
    },
    [checkGroupId]
  )

  const interactiveClickOnComplete = useCallback(async () => {
    await firebaseControlQuery.update({
      [`houseAndLand.sidePanelFolded`]: true,
    })
    await firebaseControlQuery.updateRoute('stages')
  }, [])

  const getLabel = useCallback((poly: Polygon) => {
    const POLY_TYPE = poly.type || ''
    if (
      POLY_TYPE === ARROW_TYPE ||
      POLY_TYPE === ARROW_SMALL_TYPE ||
      POLY_TYPE === MarkerType.oblongLabel ||
      POLY_TYPE === MarkerType.oblongStatus
    ) {
      return poly.label || poly.groupId
    }
    return `${poly.label} ${poly.groupId}`
  }, [])

  const setInteractiveAction = useCallback(
    (areaViewMap: MapContent) => ({
      ...areaViewMap,
      polygons: areaViewMap.polygons
        .filter((poly) =>
          findRelevantStage(poly.groupId?.split('-')?.[1] || poly.groupId)
        )
        .map((poly) => ({
          ...poly,
          label: getLabel(poly),
          isHidden: getStageLotCount(poly) === 0,
          subLabel: poly?.subLabel
            ? `${getStageLotCount(poly)} ${poly.subLabel}`
            : '',
          postFix: `- Available Lots (${getAvailableLotCount(poly)})`,
          onClick: () => interactiveClick(poly),
          onComplete: () => interactiveClickOnComplete(),
          markerColour: levelMarkerColour,
        })),
    }),
    [
      findRelevantStage,
      getStageStatus,
      getLabel,
      getStageLotCount,
      getAvailableLotCount,
      interactiveClick,
      interactiveClickOnComplete,
      levelMarkerColour,
      statusLabels,
    ]
  )

  const precinct = useMemo(() => {
    if (!(storeActivePrecinct === '' || storeActivePrecinct)) {
      return []
    }
    if (!storePrecincts[storeActivePrecinct]) {
      return (
        Object.values(storePrecincts)?.[0]?.map((res) =>
          setInteractiveAction(res)
        ) || []
      )
    }
    return (
      storePrecincts[storeActivePrecinct]?.map((res) =>
        setInteractiveAction(res)
      ) || []
    )
  }, [storeActivePrecinct, storePrecincts, setInteractiveAction, matrixData])

  const goBackToArea = async () => {
    await firebaseControlQuery.update({
      [`houseAndLand.activePrecinctId`]: '',
    })

    if (interactiveAreaViewQueryResult?.maps?.areaView?.image) {
      await firebaseControlQuery.updateRoute('area-view-house-and-land')
      return
    }
    await firebaseControlQuery.updateRoute('vision')
  }

  const precinctOptions = useMemo(
    () =>
      precinctList.map((item) => ({
        label: item.label,
        value: item.label,
      })),
    [precinctList]
  )
  const onChangeZone = useCallback(
    async (item: string) => {
      setActivePrecinct(item)
      await firebaseControlQuery.update({
        [`houseAndLand.activePrecinctId`]: item,
      })
    },
    [precinctList]
  )

  useEffect(() => {
    if (!rendering) {
      canvasRef?.current?.setPolyActive()
    }
  }, [precinct, rendering])

  useEffect(() => {
    if (interactiveQueryResult.status === Status.PENDING) {
      setImageLoaded(false)
    }

    if (interactiveQueryResult.status === Status.FULFILLED) {
      setHasLoaded(true)
    }
  }, [interactiveQueryResult.status])

  useEffect(() => {
    if (prevActivePrecinctRef.current === storeActivePrecinct) {
      return
    }
    prevActivePrecinctRef.current = storeActivePrecinct
    setTimeout(() => {
      canvasRef?.current?.setCanvas()
    }, 100)
  }, [storeActivePrecinct])

  useEffect(() => {
    if (session) {
      const {
        houseAndLand: { activePrecinctId: firebaseActivePrecinct },
      } = session

      setActivePrecinct(firebaseActivePrecinct)

      if (!hasSessionInitialized) {
        setFilterActivePrecinct(firebaseActivePrecinct)
        setHasSessionInitialized(true)
      }
    }
  }, [session?.houseAndLand?.activePrecinctId])

  useEffect(() => {
    setRenderCanvas(true)
  }, [])

  const dataHandlerPayload = useMemo(() => {
    if (
      interactiveQueryResult.status === Status.UNINITIALIZED &&
      interactiveAreaViewQueryResult.status === Status.UNINITIALIZED
    ) {
      return matrixQueryResult
    }

    return interactiveQueryResult
  }, [
    interactiveQueryResult,
    matrixQueryResult,
    interactiveAreaViewQueryResult,
  ])

  return (
    <Container>
      <>
        <DataHandler
          payloads={{
            ...dataHandlerPayload,
            status: hasLoaded ? Status.FULFILLED : dataHandlerPayload.status,
            data: Object.keys(storePrecincts),
          }}
          skeletonFrame={<PrecinctSkeleton />}
        >
          <div className="absolute left-5 top-5 z-20">
            <div className="flex items-center gap-4">
              <button
                onClick={goBackToArea}
                type="button"
                className="rounded bg-white p-1 drop-shadow-40"
              >
                <ArrowSvg className="h-8 w-8" strokeColor="#000" />
              </button>
              <button
                data-testid="toggle-filter"
                onClick={() => toggleFilter(!isFilterOpen)}
                type="button"
                className="rounded bg-white p-2.5 drop-shadow-40"
              >
                <FilterSvg className="h-5 w-5" />
              </button>
              {precinctList.length > 1 && (
                <ZoneDropdown
                  activeZone={storeActivePrecinct}
                  zoneOptions={precinctOptions}
                  onChange={onChangeZone}
                />
              )}
            </div>
          </div>

          <ImageHandler
            key={precinct[0]?.image}
            url={precinct[0]?.image}
            type="new"
            className="background-cover image-blur absolute inset-0 z-2"
            noSpliceUrl
            showFallbackImage={false}
            bgProps={{
              gradiant: 0.5,
            }}
          />

          <div className="absolute inset-0 z-3">
            <div
              ref={canvasContainerRef}
              className={`relative m-auto flex h-full w-full items-center justify-center ${
                isCanvasImageLoaded ? 'opacity-100' : 'opacity-0'
              }`}
            >
              {canvasContainerRef && renderCanvas && precinct[0] && (
                <CanvasInteractive
                  id="level-canvas"
                  ref={canvasRef}
                  canvasData={precinct[0]}
                  parentRef={canvasContainerRef}
                  hasLabel={false}
                  labelPrefix="Stage:"
                  isRendering={setRendering}
                  theme={{
                    brandColour: themeData.mainColour || '',
                    font: themeData.font || '',
                  }}
                  adjustCanvasSizeWithContainer
                  setImageLoaded={setImageLoaded}
                />
              )}
            </div>
          </div>
        </DataHandler>

        {session && (
          <Filter
            isOpen={isFilterOpen}
            toggle={toggleFilter}
            activePrecinct={filterActivePrecinct}
            firebaseActiveStage={session.houseAndLand.activeStageId}
            firebaseLotFilter={session.houseAndLand.lotFilter}
            hideStages
          />
        )}
      </>
    </Container>
  )
}

export default connect(
  ({
    firestore: { session },
    houseAndLand: { precinctList },
    projectIdentity,
  }: RootStateTypeExtra) => ({
    session,
    projectIdentity,
    precinctList,
  })
)(Precinct)
